import { useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { InboxNotificationSchema } from '@taqon/contracts';
import { getPage } from '../../shared/api/client';
import { useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import {
  absoluteTime,
  INBOX,
  INBOX_KEY,
  relativeTime,
  subjectPath,
  useInboxActions,
} from './inbox';

const PAGE_SIZES = [25, 50, 100];

/**
 * The caller's own in-system notifications (FRD-CORE-025): all or unread, paged by the API, each
 * marked read on request and linked to its record where the session may open it.
 */
export function NotificationsPage() {
  const session = useSession();
  const me = session.data;
  const csrf = me?.csrfToken ?? '';
  const timeZone = me?.businessTimezone ?? 'Asia/Dubai';
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const { markRead, markAllRead } = useInboxActions(csrf);

  const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
  if (unreadOnly) params.set('filter[unread]', 'true');
  const inbox = useQuery({
    queryKey: [...INBOX_KEY, 'page', unreadOnly, page, pageSize],
    queryFn: () => getPage(`${INBOX}?${params.toString()}`, InboxNotificationSchema),
    placeholderData: keepPreviousData,
  });
  const failure = markRead.error ?? markAllRead.error;

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Notifications"
        description="What needs your attention. Sensitive values are always masked here."
        actions={
          <Button
            variant="outlined"
            startIcon={<DoneAllIcon />}
            disabled={markAllRead.isPending || inbox.data?.total === 0}
            onClick={() => {
              markAllRead.mutate();
            }}
          >
            Mark all read
          </Button>
        }
      />
      {failure !== null && <ErrorState message={failure.message} />}
      {inbox.isError && <ErrorState message={inbox.error.message} />}
      <Tabs
        value={unreadOnly ? 1 : 0}
        onChange={(_, value: number) => {
          setUnreadOnly(value === 1);
          setPage(0);
        }}
        aria-label="Which notifications"
      >
        <Tab label="All" />
        <Tab label="Unread" />
      </Tabs>
      <Section>
        {inbox.isPending && <LoadingState label="Loading notifications" />}
        {inbox.data?.items.length === 0 && (
          <EmptyState
            title={unreadOnly ? 'Nothing unread' : 'No notifications yet'}
            detail="Notifications appear here when something needs your attention."
          />
        )}
        {inbox.data !== undefined && inbox.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Notifications">
              <TableHead>
                <TableRow>
                  <TableCell>Notification</TableCell>
                  <TableCell>Record</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inbox.data.items.map((notification) => {
                  const unread = notification.deliveryStatus === 'sent';
                  const path = subjectPath(me, notification);
                  return (
                    <TableRow key={notification.id} hover>
                      <TableCell sx={{ minWidth: 240 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ fontWeight: unread ? 700 : 400 }}
                          >
                            {notification.title}
                          </Typography>
                          {unread && <Chip size="small" color="primary" label="Unread" />}
                        </Stack>
                        {notification.body !== '' && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line', mt: 0.5 }}
                          >
                            {notification.body}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {path === null ? (
                          notification.subjectLabel
                        ) : (
                          <RecordLink to={path}>{`Open ${notification.subjectLabel}`}</RecordLink>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={absoluteTime(notification.createdAt, timeZone)}>
                          <span>{relativeTime(notification.createdAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {unread ? (
                          <Button
                            size="small"
                            disabled={markRead.isPending}
                            onClick={() => {
                              markRead.mutate(notification.id);
                            }}
                          >
                            Mark read
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Read
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={inbox.data.total}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onPageChange={(_, next) => {
                setPage(next);
              }}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}
